<template>
  <div class="guide">

    <!-- Hero -->
    <section class="hero-section">
      <div class="hero-inner">
        <h1 class="hero-title">毕设代做防骗指南</h1>
        <p class="hero-desc">
          在寻找技术服务时，保护自身权益很关键。<br/>
          本指南为您提供实用建议，帮您识别低质量服务和潜在风险，确保交易安全与项目质量。
        </p>
      </div>
    </section>

    <!-- 核心防骗准则 -->
    <section class="section bg-gray">
      <div class="section-inner">
        <div class="section-title-row">
          <div class="title-icon bg-indigo">
            <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>
          </div>
          <h2 class="section-h2">核心防骗准则</h2>
        </div>
        <p class="section-sub">掌握以下四个基本原则，大幅降低受骗风险。</p>
        <div class="grid-4">
          <div class="card" v-for="item in principles" :key="item.title">
            <div class="card-top">
              <div class="card-icon" :style="{ background: item.iconBg }">
                <svg fill="currentColor" viewBox="0 0 24 24" :style="{ color: item.iconColor }">
                  <path :d="item.icon"/>
                </svg>
              </div>
              <h3 class="card-title">{{ item.title }}</h3>
            </div>
            <p class="card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 交易安全建议 -->
    <section class="section bg-dot">
      <div class="section-inner">
        <div class="section-title-row">
          <div class="title-icon bg-blue">
            <svg fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
          </div>
          <h2 class="section-h2">交易安全建议</h2>
        </div>
        <p class="section-sub">遵循以下建议，保障交易过程安全可靠。</p>
        <div class="timeline">
          <div class="timeline-item" v-for="(item, index) in safetyTips" :key="item.title">
            <div class="timeline-left">
              <div class="timeline-num">{{ String(index+1).padStart(2,'0') }}</div>
              <div class="timeline-line" v-if="index < safetyTips.length - 1" :style="{ background: item.lineColor }"></div>
            </div>
            <div class="timeline-body" :style="{ borderLeftColor: item.borderColor, background: item.bg }">
              <h3 class="timeline-title">{{ item.title }}</h3>
              <p class="timeline-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 注意辨别低质量ABO项目 -->
    <section class="section bg-gray">
      <div class="section-inner">
        <div class="section-title-row">
          <div class="title-icon bg-red">
            <svg fill="currentColor" viewBox="0 0 24 24"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
          </div>
          <div>
            <h2 class="section-h2">注意辨别低质量"ABO"项目</h2>
            <p class="section-en">IMPORTANT REMINDER</p>
          </div>
        </div>
        <div class="warn-summary">
          <span class="warn-bold">核心问题总结：</span>页面丑陋、毫无审美、屎山代码、拼音字段、二开困难、自己不懂、导师不认、售后不管、骗完就跑
        </div>
        <div class="grid-2">
          <div class="abo-card" v-for="item in aboFeatures" :key="item.title">
            <div class="abo-num" :style="{ background: item.numBg, color: item.numColor }">{{ item.num }}</div>
            <div>
              <h3 class="abo-title">{{ item.title }}</h3>
              <p class="abo-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
        <div class="warn-box">
          <div class="warn-header">
            <svg fill="currentColor" viewBox="0 0 24 24" class="warn-icon"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
            <div>
              <h4 class="warn-title">下面是真实ABO案例警示，请擦亮双眼</h4>
              <p class="warn-desc">很多买过ABO项目的同学，最后都来找专业团队二次开发，或者直接重新做。因为这类项目根本无法通过答辩，导师一眼就能看出是低质量模板。</p>
              <p class="warn-desc warn-strong">请务必选择提供定制化开发服务的正规团队，确保项目的原创性、代码质量和售后保障！</p>
            </div>
          </div>
          <div class="abo-imgs">
            <div class="abo-img-item" v-for="(src, index) in aboImages" :key="index">
              <el-image
                :src="src"
                :preview-src-list="aboImages"
                fit="cover"
                style="width:100%;height:100%;"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'Guide',
  data() {
    return {
      principles: [
        { title: '一分钱一分货', desc: '定制开发需要技术和时间投入，远低于市场价的报价需格外警惕，谨防低质陷阱。', iconBg: '#dbeafe', iconColor: '#2563eb', icon: 'M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z' },
        { title: '保护个人隐私', desc: '勿轻易透露学校、导师或身份信息，确认付款前请务必核实对方资质与信誉。', iconBg: '#f3e8ff', iconColor: '#9333ea', icon: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z' },
        { title: '警惕异常付款', desc: '无分期、高额定金、拒绝提供视频演示成果，这些往往是项目风险的明显征兆。', iconBg: '#fee2e2', iconColor: '#dc2626', icon: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' },
        { title: '不轻信包过', desc: '所谓"绝对包过"往往是营销话术。答辩结果由你自己决定，任何打包票的服务都不可信。', iconBg: '#ccfbf1', iconColor: '#0d9488', icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z' },
      ],
      safetyTips: [
        { title: '分阶段付款机制', desc: '强烈建议采用"低定金 + 尾款"的付款方式，定金比例不超过30%，待项目完成并验收通过后再支付尾款。不要一次性支付全部费用，避免全款或高比例首付。', borderColor: '#2563eb', bg: '#eff6ff', lineColor: '#2563eb' },
        { title: '视频或远程验货', desc: '项目开发完成后，必须要求拍视频验收。在支付尾款前，务必通过视频会议或远程控制的方式，现场查看代码运行情况，确保功能符合需求且无严重Bug。验收通过后，再支付剩余费用。', borderColor: '#0891b2', bg: '#ecfeff', lineColor: '#0891b2' },
        { title: '源码交付前提', desc: '明确约定交付内容必须包含完整的源代码、数据库脚本及必要的运行说明文档。拒绝仅提供编译后可执行文件的行为，确保您拥有项目的完整控制权和后续维护能力。', borderColor: '#9333ea', bg: '#faf5ff', lineColor: '#9333ea' },
      ],
      aboFeatures: [
        { num: '1', title: '页面丑陋，无审美观念', desc: '界面设计粗糙，布局混乱，配色难看，完全没有现代化的UI设计理念，一眼就能看出是低质量模板。', numBg: '#fee2e2', numColor: '#dc2626' },
        { num: '2', title: '展示数据全是 xxx1、xxx2、xxx3', desc: '测试数据敷衍了事，所有示例数据都是"测试1"、"用户2"、"商品3"这种毫无意义的占位符。', numBg: '#ffedd5', numColor: '#ea580c' },
        { num: '3', title: '数据库字段全是拼音命名', desc: '如 yonghuming、mima、shouji 等，不符合规范，普通学生维护起来困难重重。', numBg: '#fef9c3', numColor: '#ca8a04' },
        { num: '4', title: 'Node.js 版本过低（8.x-14.x）', desc: '使用过时的技术栈，导致很多依赖包无法安装，项目无法在新环境中运行。', numBg: '#fee2e2', numColor: '#dc2626' },
        { num: '5', title: '前后端不分离', desc: '前端代码臃肿，后端代码混乱，代码结构混乱，完全不利于维护和二次开发。', numBg: '#f3e8ff', numColor: '#9333ea' },
        { num: '6', title: '一套代码卖几千个项目', desc: '一般项目账号和密码是abo，无数个系统仅用这一套代码，导致代码重复率极高，查重必挂，答辩必败。', numBg: '#fce7f3', numColor: '#db2777' },
      ],
      aboImages: [
        require('@/assets/abo1.jpg'),
        require('@/assets/abo2.jpg'),
        require('@/assets/abo3.jpg'),
        require('@/assets/abo4.jpg'),
        require('@/assets/abo5.jpg'),
        require('@/assets/abo6.jpg'),
        require('@/assets/abo7.jpg'),
        require('@/assets/abo8.jpg'),
        require('@/assets/abo9.jpg'),
        require('@/assets/abo10.jpg'),
        require('@/assets/abo11.jpg'),
      ]
    }
  },
  created() {
    this.increasePageView()
  },
  methods: {
    increasePageView() {
      this.request.get('/pageView/increase', {
        params: { pageName: 'guide' }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.guide { background: #fff; }

/* Hero */
.hero-section {
  background: #fff;
  background-image: linear-gradient(to right, #f0f0f0 1px, transparent 1px),
    linear-gradient(to bottom, #f0f0f0 1px, transparent 1px);
  background-size: 20px 20px;
  display: flex; align-items: center; justify-content: center;
  padding: 40px 20px; min-height: 300px;
}
.hero-inner { max-width: 960px; margin: 0 auto; text-align: center; }
.hero-title { font-size: 36px; font-weight: 900; color: #4f46e5; margin: 0 0 24px; }
@media (min-width: 768px) { .hero-title { font-size: 48px; } }
.hero-desc { font-size: 18px; color: #4b5563; line-height: 1.75; margin: 0; }

/* 通用 */
.section { padding: 64px 0; }
.bg-gray { background: #f9fafb; }
.bg-dot {
  background: #fff;
  background-image: radial-gradient(circle, #d1d5db 1px, transparent 1px);
  background-size: 30px 30px;
}
.section-inner { max-width: 1152px; margin: 0 auto; padding: 0 20px; }
@media (min-width: 768px) { .section-inner { padding: 0 32px; } }

.section-title-row { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.title-icon {
  width: 40px; height: 40px; display: flex; align-items: center;
  justify-content: center; flex-shrink: 0;
}
.title-icon svg { width: 24px; height: 24px; color: #fff; }
.bg-indigo { background: #4f46e5; }
.bg-blue   { background: #2563eb; }
.bg-red    { background: #dc2626; }
.section-h2 { font-size: 28px; font-weight: 900; color: #111827; margin: 0; }
@media (min-width: 768px) { .section-h2 { font-size: 30px; } }
.section-sub { font-size: 14px; color: #6b7280; margin: 0 0 24px; }
.section-en { font-size: 12px; color: #9ca3af; margin: 2px 0 0; }

/* 核心准则 4列 */
.grid-4 {
  display: grid; grid-template-columns: 1fr 1fr; gap: 24px;
}
@media (min-width: 1024px) { .grid-4 { grid-template-columns: repeat(4, 1fr); } }

.card { background: #fff; border: 1px solid #e5e7eb; padding: 24px; transition: border-color 0.2s; }
.card:hover { border-color: #2563eb; }
.card-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.card-icon { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.card-icon svg { width: 24px; height: 24px; }
.card-title { font-size: 18px; font-weight: 700; color: #111827; margin: 0; }
.card-desc { font-size: 14px; color: #4b5563; line-height: 1.625; margin: 0; }

/* 时间线 */
.timeline { display: flex; flex-direction: column; gap: 0; }
.timeline-item { display: flex; gap: 24px; }
.timeline-left { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; }
.timeline-num {
  width: 48px; height: 48px; background: #f3f4f6;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 18px; color: #4b5563;
}
.timeline-line { width: 4px; flex: 1; min-height: 16px; }
.timeline-body {
  flex: 1; padding: 24px; border-left: 4px solid; margin-bottom: 32px;
}
.timeline-title { font-size: 20px; font-weight: 700; color: #111827; margin: 0 0 12px; }
.timeline-desc { font-size: 14px; color: #4b5563; line-height: 1.75; margin: 0; }

/* ABO 特征 2列 */
.grid-2 { display: grid; grid-template-columns: 1fr; gap: 24px; margin-bottom: 32px; }
@media (min-width: 768px) { .grid-2 { grid-template-columns: 1fr 1fr; } }

.abo-card { background: #fff; border: 1px solid #e5e7eb; padding: 24px; display: flex; gap: 12px; align-items: flex-start; }
.abo-num {
  width: 32px; height: 32px; display: flex; align-items: center;
  justify-content: center; font-weight: 700; flex-shrink: 0; font-size: 14px;
}
.abo-title { font-size: 16px; font-weight: 700; color: #111827; margin: 0 0 8px; }
.abo-desc { font-size: 14px; color: #4b5563; line-height: 1.625; margin: 0; }

/* 警告框 */
.warn-summary {
  background: #fef2f2; border: 1px solid #fecaca;
  padding: 16px 24px; font-size: 15px; color: #7f1d1d;
  line-height: 1.75; margin-bottom: 32px;
}
.warn-bold { font-weight: 700; }

.warn-box { background: #fef2f2; border: 1px solid #fecaca; padding: 24px; }
.warn-header { display: flex; gap: 12px; align-items: flex-start; margin-bottom: 24px; }
.warn-icon { width: 24px; height: 24px; color: #dc2626; flex-shrink: 0; margin-top: 2px; }
.warn-title { font-size: 18px; font-weight: 700; color: #7f1d1d; margin: 0 0 8px; }
.warn-desc { font-size: 14px; color: #991b1b; line-height: 1.75; margin: 0 0 8px; }
.warn-strong { font-weight: 700; }

.abo-imgs { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
@media (min-width: 768px) { .abo-imgs { grid-template-columns: repeat(4, 1fr); } }
.abo-img-item {
  background: #e5e7eb; aspect-ratio: 16/9;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; color: #6b7280; overflow: hidden;
}
.abo-img-item { cursor: pointer; }
.abo-img-item:hover img { opacity: 0.85; }
.abo-img-item img { width: 100%; height: 100%; object-fit: cover; transition: opacity 0.2s; }

/* 灯箱 */
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: pointer;
}

.lightbox img {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  cursor: default;
}

.lightbox-close {
  position: absolute;
  top: 20px;
  right: 28px;
  color: #fff;
  font-size: 28px;
  cursor: pointer;
  line-height: 1;
}
</style>
